import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Play, Download, RefreshCw, CheckCircle, Clock, AlertCircle, Maximize2 } from 'lucide-react';
import { useWorkflows } from '../../hooks/useWorkflows';
import { useWorkflow } from '../../hooks/useWorkflow.ts';
import { WorkflowStep, InputSpec } from '../../types/workflow';
import { DataVisualization } from '../../components/ui/DataVisualization';
import { useExecutionWebSocket } from '../../hooks/useWebSocket';
import { executionApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { generateMockResults } from '../../utils/mockData';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';

export function ExecuteWorkflow() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const preselectedWorkflowId = searchParams.get('workflow');
  
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>(preselectedWorkflowId || '');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [executionResults, setExecutionResults] = useState<Record<string, any>>({});
  const [uploadedFiles] = useState<Record<string, string>>({});
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [executionLogs, setExecutionLogs] = useState<string[]>([]);
  const [fullScreenData, setFullScreenData] = useState<{stepId: string; results: any} | null>(null);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  // Load workflows and selected workflow details
  const { data: workflows, isLoading: workflowsLoading, error: workflowsError } = useWorkflows();
  const { data: selectedWorkflowData } = useWorkflow(selectedWorkflow);

  // WebSocket connection for real-time execution updates
  const { isConnected } = useExecutionWebSocket(currentExecutionId || undefined);

  // Set default values when workflow is loaded
  useEffect(() => {
    if (selectedWorkflowData && selectedWorkflowData.steps) {
      const defaultValues: Record<string, any> = {};
      
      selectedWorkflowData.steps.forEach((step: WorkflowStep) => {
        Object.entries(step.inputs).forEach(([inputKey, inputSpec]: [string, InputSpec]) => {
          if (inputSpec.default !== null && inputSpec.default !== undefined) {
            defaultValues[`${step.step_id}_${inputKey}`] = inputSpec.default;
          }
        });
      });
      
      setFormData(prev => ({ ...defaultValues, ...prev }));
    }
  }, [selectedWorkflowData]);

  const handleWorkflowSelect = (workflowId: string) => {
    setSelectedWorkflow(workflowId);
    setFormData({});
  };

  const handleExecute = async () => {
    if (!selectedWorkflow || !selectedWorkflowData) {
      toast({
        variant: "destructive",
        title: 'No workflow selected',
        description: 'Please select a workflow before executing',
      });
      return;
    }

    // Validate required inputs
    const missingRequiredInputs: string[] = [];
    selectedWorkflowData.steps.forEach((step: WorkflowStep) => {
      Object.entries(step.inputs).forEach(([inputKey, inputSpec]: [string, InputSpec]) => {
        if (inputSpec.required && !inputSpec.from_step) {
          const formKey = `${step.step_id}_${inputKey}`;
          const value = formData[formKey] || uploadedFiles[formKey];
          if (!value || value === '') {
            missingRequiredInputs.push(`${step.name}: ${inputSpec.label}`);
          }
        }
      });
    });

    if (missingRequiredInputs.length > 0) {
      toast({
        variant: "destructive",
        title: 'Missing required inputs',
        description: `Please fill in the following fields: ${missingRequiredInputs.join(', ')}`,
      });
      return;
    }
    
    try {
      // Show loading toast
      toast({
        title: 'Initializing workflow execution...',
        description: 'Please wait while we prepare your workflow',
      });

      setIsExecuting(true);
      setExecutionProgress(0);
      setCurrentStep('Creating execution...');
      setExecutionLogs([]);
      setExecutionResults({});

      // Prepare execution inputs from form data
      const executionInputs: Record<string, any> = {};
      
      // Collect all form inputs
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          executionInputs[key] = value;
        }
      });

      // Add uploaded file paths
      Object.entries(uploadedFiles).forEach(([key, filePath]) => {
        executionInputs[key] = filePath;
      });

      toast({
        title: 'Validating inputs and creating execution...',
        description: 'This may take a moment',
      });

      // Create execution via API
      const execution = await executionApi.createExecution({
        workflow_id: selectedWorkflow,
        inputs: executionInputs
      });

      setCurrentExecutionId(execution.execution_id);
      setExecutionLogs(prev => [...prev, `✅ Execution ${execution.execution_id} created successfully`]);
      setCurrentStep('Execution started');
      
      toast({
        title: 'Workflow execution started!',
        description: `Execution ID: ${execution.execution_id}`,
      });
      
      // The WebSocket connection will handle real-time updates from here
      // For demo purposes, we'll also simulate some progress
      await simulateExecution(selectedWorkflowData);
      
    } catch (error: any) {
      console.error('Execution failed:', error);
      
      let errorMessage = 'Unknown error occurred';
      let errorDescription = 'Please try again or contact support';
      
      if (error.response) {
        // API error with response
        errorMessage = error.response.data?.detail || 'Server error';
        errorDescription = `Status: ${error.response.status}`;
        
        if (error.response.status === 400) {
          errorMessage = 'Invalid request';
          errorDescription = 'Please check your input parameters and try again';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication required';
          errorDescription = 'Please refresh the page and try again';
        } else if (error.response.status === 404) {
          errorMessage = 'Workflow not found';
          errorDescription = 'The selected workflow may have been deleted';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error';
          errorDescription = 'The server is experiencing issues. Please try again later';
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error';
        errorDescription = 'Unable to connect to the server. Please check your connection';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setExecutionLogs(prev => [...prev, `❌ Execution failed: ${errorMessage}`]);
      setIsExecuting(false);
      setCurrentStep('Failed');
      
      toast({
        variant: "destructive",
        title: 'Execution failed',
        description: errorDescription,
      });
    }
  };

  const simulateExecution = async (workflowData: any) => {
    try {
      // Simulate execution progress for demo purposes
      for (let i = 0; i < workflowData.steps.length; i++) {
        const step = workflowData.steps[i];
        setCurrentStep(step.name);
        setExecutionProgress(((i + 1) / workflowData.steps.length) * 100);
        setExecutionLogs(prev => [...prev, `🔄 Executing step: ${step.name}`]);
        
        // Simulate step execution time with potential for random delays
        const executionTime = Math.random() * 3000 + 1000; // 1-4 seconds
        await new Promise(resolve => setTimeout(resolve, executionTime));
        
        // Simulate occasional step warnings or issues (for demo)
        if (Math.random() < 0.1) { // 10% chance of a warning
          setExecutionLogs(prev => [...prev, `⚠️ Warning in ${step.name}: Minor issue detected but continuing`]);
          toast({
            variant: "default",
            title: `Step Warning: ${step.name}`,
            description: 'Minor issue detected but execution continues',
          });
        }
        
        // Simulate step completion with mock results
        const mockResults = generateMockResults(step);
        setExecutionResults(prev => ({
          ...prev,
          [step.step_id]: mockResults
        }));
        
        setExecutionLogs(prev => [...prev, `✅ Step ${step.name} completed successfully`]);
        toast({
          title: `Step completed: ${step.name}`,
          description: `Progress: ${Math.round(((i + 1) / workflowData.steps.length) * 100)}%`,
        });
      }

      setCurrentStep('Completed');
      setIsExecuting(false);
      setExecutionLogs(prev => [...prev, '🎉 Workflow execution completed successfully!']);
      
      toast({
        title: 'Workflow execution completed!',
        description: 'All steps executed successfully. Results are ready for download.',
      });
      
    } catch (error: any) {
      console.error('Simulation error:', error);
      setIsExecuting(false);
      setCurrentStep('Failed');
      setExecutionLogs(prev => [...prev, `❌ Execution failed: ${error.message || 'Unknown error'}`]);
      
      toast({
        variant: "destructive",
        title: 'Workflow execution failed',
        description: 'An unexpected error occurred during execution',
      });
    }
  };


  const handleInputChange = (stepId: string, inputKey: string, value: any) => {
    const formKey = `${stepId}_${inputKey}`;
    setFormData(prev => ({
      ...prev,
      [formKey]: value
    }));
  };

  const renderInputField = (stepId: string, inputKey: string, inputSpec: InputSpec) => {
    const formKey = `${stepId}_${inputKey}`;
    const value = formData[formKey] || inputSpec.default || '';

    switch (inputSpec.type) {
      case 'select':
        return (
          <div key={formKey} className="space-y-2">
            <Label htmlFor={formKey}>
              {inputSpec.label}
              {inputSpec.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value}
              onValueChange={(newValue) => handleInputChange(stepId, inputKey, newValue)}
            >
              <SelectTrigger id={formKey}>
                <SelectValue placeholder={`Select ${inputSpec.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {inputSpec.options?.map((option: string) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {inputSpec.description && (
              <p className="text-sm text-gray-500">{inputSpec.description}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={formKey} className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={formKey}
                checked={value}
                onCheckedChange={(checked) => handleInputChange(stepId, inputKey, checked)}
              />
              <Label htmlFor={formKey} className="text-sm font-medium">
                {inputSpec.label}
                {inputSpec.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
            </div>
            {inputSpec.description && (
              <p className="text-sm text-gray-500 ml-6">{inputSpec.description}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={formKey} className="space-y-2">
            <Input
              type="number"
              label={inputSpec.label}
              value={value}
              onChange={(e) => handleInputChange(stepId, inputKey, Number(e.target.value))}
              required={inputSpec.required}
              description={inputSpec.description}
            />
          </div>
        );

      case 'csv':
      case 'file':
        return (
          <div key={formKey} className="space-y-2">
            <Label htmlFor={formKey}>
              {inputSpec.label}
              {inputSpec.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={formKey}
                type="file"
                accept={inputSpec.type === 'csv' ? '.csv' : '*'}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleInputChange(stepId, inputKey, file);
                  }
                }}
                className="flex-1"
              />
              {value && (
                <Badge variant="secondary" className="whitespace-nowrap">
                  Selected
                </Badge>
              )}
            </div>
            {inputSpec.description && (
              <p className="text-sm text-gray-500">{inputSpec.description}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={formKey} className="space-y-2">
            <Input
              label={inputSpec.label}
              value={value}
              onChange={(e) => handleInputChange(stepId, inputKey, e.target.value)}
              required={inputSpec.required}
              description={inputSpec.description}
            />
          </div>
        );
    }
  };

  const getStepIcon = (index: number, total: number) => {
    const stepProgress = (index + 1) / total * 100;
    if (executionProgress >= stepProgress) {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (executionProgress > (index / total * 100)) {
      return <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />;
    } else {
      return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  // Show loading state
  if (workflowsLoading) {
    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Execute Workflow
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Loading workflows...
            </p>
          </div>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state
  if (workflowsError) {
    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Execute Workflow
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Select and execute a workflow with custom parameters
            </p>
          </div>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Failed to Load Workflows
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {workflowsError?.message || 'Unable to connect to the backend server'}
              </p>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Execute Workflow
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Select and execute a workflow with custom parameters
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Workflow Selection and Step Parameters */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Play className="h-5 w-5 mr-2" />
                Select Workflow
              </CardTitle>
              <CardDescription>
                Choose a workflow to execute with custom parameters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-4">
                {workflows && workflows.map((workflow) => (
                  <AccordionItem
                    key={workflow.workflow_id}
                    value={workflow.workflow_id}
                    className={`border rounded-lg ${
                      selectedWorkflow === workflow.workflow_id
                        ? 'border-primary bg-primary/5'
                        : 'border-border'
                    }`}
                  >
                    <AccordionTrigger
                      className={`p-4 hover:no-underline ${
                        selectedWorkflow === workflow.workflow_id ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => handleWorkflowSelect(workflow.workflow_id)}
                    >
                      <div className="flex items-start justify-between w-full text-left">
                        <div>
                          <h3 className="text-sm font-medium text-foreground">
                            {workflow.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {workflow.description}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Estimated time: {workflow.estimated_duration}
                          </p>
                        </div>
                        <div className="flex items-center ">
                          <div className="text-sm text-muted-foreground">
                            {workflow.steps_count} steps
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 pt-0">
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium text-foreground">Workflow ID:</span>
                          <span className="text-muted-foreground font-mono">{workflow.workflow_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-foreground">Created:</span>
                          <span className="text-muted-foreground">
                            {workflow.created_at ? new Date(workflow.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-foreground">Last Updated:</span>
                          <span className="text-muted-foreground">
                            {workflow.updated_at ? new Date(workflow.updated_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        {workflow.tags && workflow.tags.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-foreground">Tags:</span>
                            <div className="flex flex-wrap gap-1">
                              {workflow.tags.map((tag: string) => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Dynamic Parameters */}
          {selectedWorkflowData && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="step-parameters" className="border rounded-lg">
                <AccordionTrigger className="p-4 hover:no-underline">
                  <CardTitle className="flex items-center text-left">
                    Step Parameters
                  </CardTitle>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 pt-0">
                  <Card>
                    <CardHeader>
                      <CardDescription>
                        Configure parameters for each workflow step
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Tabs defaultValue={selectedWorkflowData.steps[0]?.step_id || ''} className="w-full">
                        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3 mb-6">
                          {selectedWorkflowData.steps.map((step: WorkflowStep, stepIndex: number) => (
                            <TabsTrigger
                              key={step.step_id}
                              value={step.step_id}
                              className="text-xs"
                            >
                              <div className="flex flex-col items-center space-y-1">
                                <span className="font-medium">Step {stepIndex + 1}</span>
                                <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                                  {step.name}
                                </span>
                              </div>
                            </TabsTrigger>
                          ))}
                        </TabsList>
                        {selectedWorkflowData.steps.map((step: WorkflowStep, stepIndex: number) => (
                          <TabsContent key={step.step_id} value={step.step_id} className="space-y-4">
                            <div className="bg-muted border rounded-lg p-4">
                              <h4 className="text-sm font-semibold text-foreground mb-2">
                                {stepIndex + 1}. {step.name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {step.description || 'Configure parameters for this workflow step'}
                              </p>
                            </div>
                            <div className="space-y-4">
                              {Object.entries(step.inputs).map(([inputKey, inputSpec]: [string, InputSpec]) => {
                                // Skip inputs that come from previous steps
                                if (inputSpec.from_step) {
                                  return null;
                                }
                                return (
                                  <div key={`${step.step_id}_${inputKey}`} className="bg-card border rounded-lg p-4">
                                    {renderInputField(step.step_id, inputKey, inputSpec)}
                                  </div>
                                );
                              })}
                            </div>
                            {Object.entries(step.inputs).filter(([_, inputSpec]) => !inputSpec.from_step).length === 0 && (
                              <div className="text-center py-8 text-muted-foreground">
                                <p className="text-sm">No configurable parameters for this step.</p>
                                <p className="text-xs mt-1">All inputs are automatically provided from previous steps.</p>
                              </div>
                            )}
                          </TabsContent>
                        ))}
                      </Tabs>
                    </CardContent>
                  </Card>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* Execution Control - Moved to bottom after step parameters */}
          {selectedWorkflowData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Execution Control
                </CardTitle>
                <CardDescription>
                  Monitor workflow execution progress and real-time logs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Execute Button */}
                  <Button
                    onClick={handleExecute}
                    disabled={isExecuting}
                    loading={isExecuting}
                    className="w-full py-3 text-lg font-medium"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    {isExecuting ? 'Executing...' : 'Execute Workflow'}
                  </Button>

                  {/* Progress */}
                  {isExecuting && (
                    <div className="space-y-4 p-4 bg-muted border rounded-lg">
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-foreground">Execution Progress</span>
                        <span className="text-foreground font-bold">{Math.round(executionProgress)}%</span>
                      </div>
                      <Progress value={executionProgress} className="h-3" />
                      <p className="text-sm text-foreground font-medium flex items-center">
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {currentStep}
                      </p>
                    </div>
                  )}

                  {/* Real-time Execution Logs */}
                  {(isExecuting || executionLogs.length > 0) && (
                    <div className="space-y-3 p-4 bg-muted border rounded-lg">
                      <h4 className="text-sm font-bold text-foreground flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        Execution Logs
                        {isConnected && (
                          <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                            <div className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                            Live
                          </span>
                        )}
                      </h4>
                      <div className="bg-background border rounded-lg p-4 max-h-48 overflow-y-auto">
                        {executionLogs.length > 0 ? (
                          <div className="space-y-2">
                            {executionLogs.map((log, index) => (
                              <div key={index} className="text-xs text-foreground font-mono flex items-start">
                                <span className="text-muted-foreground font-bold mr-2 flex-shrink-0">
                                  [{new Date().toLocaleTimeString()}]
                                </span>
                                <span className="flex-1">{log}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic text-center py-4">
                            Waiting for execution logs...
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Steps */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-foreground">Workflow Steps</h4>
                    <div className="space-y-2">
                      {selectedWorkflowData.steps.map((step: WorkflowStep, index: number) => (
                        <div
                          key={index}
                          className="flex items-center space-x-3 text-sm"
                        >
                          {getStepIcon(index, selectedWorkflowData.steps.length)}
                          <span
                            className={
                              executionProgress >= ((index + 1) / selectedWorkflowData.steps.length * 100)
                                ? 'text-green-600 font-medium'
                                : executionProgress > (index / selectedWorkflowData.steps.length * 100)
                                ? 'text-blue-600 font-medium'
                                : 'text-muted-foreground'
                            }
                          >
                            {step.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Results */}
                  {executionProgress === 100 && !isExecuting && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-800">
                          Workflow completed successfully!
                        </span>
                      </div>
                      <div className="mt-2">
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-2" />
                          Download Results
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {!selectedWorkflowData && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Play className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Select a Workflow
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Choose a workflow from the list to configure and execute it
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Execution Results */}
        <div className="space-y-6">
          {selectedWorkflowData && executionProgress === 100 && !isExecuting && Object.keys(executionResults).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Execution Results
                </CardTitle>
                <CardDescription>
                  Detailed results from workflow execution
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-4">
                    {Object.entries(executionResults).map(([stepId, results]) => (
                      <Card key={stepId}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="flex items-center">
                                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                                {stepId} Results
                              </CardTitle>
                              <CardDescription>
                                Output data from the {stepId} step
                              </CardDescription>
                            </div>
                            <Dialog open={isFullScreenOpen && fullScreenData?.stepId === stepId} onOpenChange={(open) => {
                              setIsFullScreenOpen(open);
                              if (!open) setFullScreenData(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setFullScreenData({ stepId, results });
                                    setIsFullScreenOpen(true);
                                  }}
                                >
                                  <Maximize2 className="h-4 w-4 mr-2" />
                                  Full Screen
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-[75vw] max-h-full overflow-scroll">
                                <DialogHeader>
                                  <DialogTitle className="flex items-center">
                                    <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                                    {stepId} Results - Full Screen View
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="h-full overflow-auto">
                                  <DataVisualization
                                    data={results}
                                    title=""
                                    description=""
                                    type="auto"
                                    maxRows={1000}
                                    onDownload={(data, format) => {
                                      const blob = new Blob([JSON.stringify(data, null, 2)], {
                                        type: format === 'csv' ? 'text/csv' : 'application/json'
                                      });
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `${stepId}_results.${format}`;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                    }}
                                  />
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <DataVisualization
                            data={results}
                            title=""
                            description=""
                            type="auto"
                            maxRows={50}
                            onDownload={(data, format) => {
                              const blob = new Blob([JSON.stringify(data, null, 2)], {
                                type: format === 'csv' ? 'text/csv' : 'application/json'
                              });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${stepId}_results.${format}`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedWorkflowData && (executionProgress < 100 || isExecuting) && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Execution Results
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Results will appear here once the workflow execution is complete
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {!selectedWorkflowData && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Play className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Select a Workflow
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Choose a workflow from the list to configure and execute it
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}